import { UserApi } from '@/apis';
import RootStore from '.';
import BaseStore from './BaseStore';
import { action, flow, makeObservable, observable } from 'mobx';
import { ResponseData } from '@/types/http';
import { TableState } from '@tanstack/react-table';
import { Account, HistoryChangeUser } from '@/types/account';
import {
    GetAccountResponse,
    GetAccountsRequest,
    GetAccountsResponse,
    GetListFavoriteLocationsByStatusRequest
} from '@/types/http-payload/account';
import { FavoriteLocation } from '@/types/location';
import { RegistedCoupon } from '@/types/coupon';

export default class AccountStore extends BaseStore {
    api: UserApi;

    accountsPagination: Account[] = [];

    accounts: Account[] = [];
    account?: GetAccountResponse;
    favoriteLocationsList: FavoriteLocation[] = [];
    nonFavoriteLocationsList: FavoriteLocation[] = [];
    totalPage: number = 0;
    registedCouponList: RegistedCoupon[] = [];

    defaultPagingHistory: TableState = {
        pagination: {
            pageIndex: 0,
            pageSize: 5
        },
        sorting: [{}]
    } as TableState;

    // history
    historyChangeUser: HistoryChangeUser[] = [];
    totalRecordHistoryChangeUser: number = 0;
    totalPageHistoryChangeUser: number = 0;
    pagingHistoryChangeUser: TableState = this.defaultPagingHistory;

    favPaging: TableState = {
        pagination: {
            pageIndex: -2,
            pageSize: 100
        },
        sorting: [{}]
    } as TableState;

    totalRecordFav: number = 0;
    totalPageFav: number = 0;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            accounts: observable,
            account: observable,
            favoriteLocationsList: observable,
            nonFavoriteLocationsList: observable,
            totalPage: observable,
            registedCouponList: observable,
            favPaging: observable,
            totalRecordFav: observable,
            totalPageFav: observable,
            getAccounts: flow.bound,
            getAccount: flow.bound,
            createAccount: flow.bound,
            editAccount: flow.bound,
            deleteAccount: flow.bound,
            uploadOCR: flow.bound,
            getListFavoriteLocationsByStatus: flow.bound,
            getListUserFavoriteLocations: flow.bound,
            registerFavoriteLocation: flow.bound,
            unregisterFavoriteLocation: flow.bound,
            registerCoupon: flow.bound,
            getRegistedCouponList: flow.bound,
            // history
            historyChangeUser: observable,
            totalRecordHistoryChangeUser: observable,
            totalPageHistoryChangeUser: observable,
            pagingHistoryChangeUser: observable,
            defaultPagingHistory: observable,
            getHisoryChangeUser: flow.bound,
            cleanHistory: action.bound,
            // list user cms
            accountsPagination: observable,
            getAccountsPagination: flow.bound
        });
        this.api = new UserApi();
    }

    * getAccountsPagination(searchParams?: GetAccountsRequest, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData<GetAccountsResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getAccountsPagination, payload);
            if (res.ok) {
                this.accountsPagination = res.data.elements;
                this.paging = {
                    ...this.paging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                };
                this.totalRecord = res.data.paginate.totalRecord;
            }
        } catch (error) {
        }
    }

    * getAccounts(searchParams?: GetAccountsRequest, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData<GetAccountsResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getAccounts, payload);
            if (res.ok) {
                this.accounts = res.data.elements;
                this.paging = {
                    ...this.paging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                };
                this.totalRecord = res.data.paginate.totalRecord;
            }
        } catch (error) {
        }
    }

    * getAccount(id: number | string) {
        try {
            const res: ResponseData<GetAccountResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getAccount, { id });
            if (res?.ok) {
                this.account = res?.data;
                return res;
            }
            return false;
        } catch (error) {
        }
    }

    * createAccount(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.createAccount, payload);
            if (res.ok) {
                if (res?.data?.[0]) {
                    this.account = res?.data?.[0];
                }
                return res;
            }
        } catch (error) {
        }
    }

    * editAccount(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.editAccount, payload);
            if (res.ok) {
                if (res?.data?.[0]) {
                    this.account = res?.data?.[0];
                }
                return res;
            }
        } catch (error) {
        }
    }

    * deleteAccount(accountId: number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.deleteAccount, accountId);
            if (res.ok) {
                return res;
            }
        } catch (error) {
        }
    }

    * uploadOCR(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.uploadOCR, payload);
            if (res.ok) {
                return res;
            }
        } catch (error) {
        }
    }

    * getListFavoriteLocationsByStatus(searchParams: GetListFavoriteLocationsByStatusRequest, paging?: TableState) {
        try {
            const { isFavorite } = searchParams;
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.getListFavoriteLocationsByStatus, payload);
            if (res.ok) {
                if (isFavorite) {
                    this.favoriteLocationsList = res.data.elements;
                } else {
                    this.nonFavoriteLocationsList = res.data.elements;
                    this.paging = {
                        ...this.paging,
                        sorting: paging?.sorting ?? [],
                        pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                    };
                    this.totalRecord = res.data.paginate.totalRecord;
                    this.totalPage = res.data.paginate.totalPage;
                }
            }
        } catch (error) {
        }
    }

    *getListUserFavoriteLocations(searchParams: GetListFavoriteLocationsByStatusRequest, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.favPaging), ...searchParams };
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.getListFavoriteLocationsByStatus, payload);
            if (res.ok) {
                this.favoriteLocationsList = res.data.elements;
                this.favPaging = {
                    ...this.favPaging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                };
                this.totalRecordFav = res.data.paginate.totalRecord;
                this.totalPageFav = res.data.paginate.totalPage;
            }
        } catch (error) {
        }
    }

    * registerFavoriteLocation(payload: { locationId: number }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.registerFavoriteLocation, payload);
            if (res.ok) {
                return true;
            }
            return false;
        } catch (error) {
        }
    }

    * unregisterFavoriteLocation(payload: { locationIds: number[] }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.unregisterFavoriteLocation, payload);
            if (res.ok) {
                return true;
            }
            return false;
        } catch (error) {
        }
    }

    * registerCoupon(payload: { couponCode: string }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.registerCoupon, payload);
            if (res.ok) {
                return true;
            }
            return false;
        } catch (error) {
        }
    }

    * getRegistedCouponList(searchParams?: any, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.getRegistedCouponList, payload);
            if (res.ok) {
                this.registedCouponList = res.data.elements;
                this.paging = {
                    ...this.paging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                };
                this.totalRecord = res.data.paginate.totalRecord;
                this.totalPage = res.data.paginate.totalPage;
            }
        } catch (error) {
        }
    }

    // #region History Change Account
    * getHisoryChangeUser(searchParams?: any, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingHistoryChangeUser
                ),
                ...searchParams
            };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getHisoryChangeUser, payload);
            if (res.ok) {
                this.historyChangeUser = res.data?.elements ?? [];
                this.pagingHistoryChangeUser = {
                    ...this.pagingHistoryChangeUser,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalRecordHistoryChangeUser = res.data.paginate.totalRecord;
                this.totalPageHistoryChangeUser = res.data.paginate.totalPage;
                return true;
            }
            return false;
        } catch (error) {
        }
    }
    // #endregion History Change Account

    clean(): void {
        super.clean();
        this.accounts = [];
        this.favoriteLocationsList = [];
        this.nonFavoriteLocationsList = [];
        this.totalPage = 0;
        this.registedCouponList = [];
    }

    cleanHistory(): void {
        this.historyChangeUser = [];
        this.totalRecordHistoryChangeUser = 0;
        this.totalPageHistoryChangeUser = 0;
        this.pagingHistoryChangeUser = this.defaultPagingHistory;
    }
}
